import { TicketListApiData, TicketRecordList, TicketStatus } from '@navio/nsdk';
import { formatLicensePlate, NoddiDateFormats, noddiFormatDate } from 'noddi-util';

import type { OrderingKeyOf } from 'noddi-async';

import { NoddiColumnDef } from 'noddi-ui-web';
import { LinkEntityType, LinkRef } from './components/linkTypes';
import { EntityChip } from './EntityChip';
import { TICKET_CATEGORY_LABELS } from './meta';
import { getTicketLinkPath, TicketApp } from './routes';
import { TicketPriorityBadge } from './TicketPriorityBadge';
import { TicketStatusBadge } from './TicketStatusBadge';

const ACTIVE_TICKET_STATUSES = new Set<string>([TicketStatus.OPEN, TicketStatus.SNOOZED]);

/** Sort keys `/v1/tickets/` accepts, from its generated nsdk `ordering` union. */
type TicketTableOrderingKey = OrderingKeyOf<TicketListApiData>;

export const getTicketListColumns = (
  app: TicketApp
): NoddiColumnDef<TicketRecordList, unknown, TicketTableOrderingKey>[] => {
  const linkedChip = (kind: LinkEntityType, ref: LinkRef, label: string) => (
    <EntityChip key={kind} kind={kind} label={label} to={getTicketLinkPath(app, kind, ref)} />
  );

  return [
    {
      id: 'id',
      header: 'ID',
      accessorKey: 'id',
      cell: ({ row }) => (
        <div className='flex items-center gap-2'>
          <TicketPriorityBadge priority={row.original.priority} showLabel={false} />
          <span className='font-mono text-xs text-muted-foreground'>T-{row.original.id}</span>
        </div>
      ),
      meta: { orderingKey: 'id' },
      size: 110
    },
    {
      id: 'title',
      header: 'Title',
      accessorKey: 'title',
      cell: ({ row }) => <span className='truncate font-medium'>{row.original.title}</span>,
      meta: { orderingKey: 'title' }
    },
    {
      id: 'assignee',
      header: 'Assignee',
      accessorFn: (row) => row.assignee?.name ?? '',
      cell: ({ row }) => (row.original.assignee ? <span className='text-sm'>{row.original.assignee.name}</span> : null),
      size: 140
    },
    {
      id: 'linked',
      header: 'Linked',
      cell: ({ row }) => {
        const chips: React.ReactNode[] = [];
        const { userGroup, booking, userGroupCar, tireQuote, storageObjectGroup } = row.original;
        if (userGroup) {
          chips.push(linkedChip('customer', { id: userGroup.id, label: userGroup.name }, userGroup.name));
        }
        if (booking) {
          const label = booking.slug ? `#${booking.slug}` : `#${booking.id}`;
          chips.push(linkedChip('booking', { id: booking.id, label }, label));
        }
        if (userGroupCar) {
          const label = formatLicensePlate(userGroupCar.licensePlate.number);
          chips.push(linkedChip('car', { id: userGroupCar.id, carId: userGroupCar.carId, label }, label));
        }
        if (tireQuote) {
          const label = tireQuote.customerUserGroupName ?? `#${tireQuote.id}`;
          chips.push(linkedChip('quote', { id: tireQuote.id, label }, label));
        }
        if (storageObjectGroup) {
          chips.push(
            linkedChip(
              'storage',
              { id: storageObjectGroup.id, label: storageObjectGroup.identifier },
              storageObjectGroup.identifier
            )
          );
        }
        const visible = chips.slice(0, 2);
        const overflow = chips.length - visible.length;
        return (
          <div className='flex flex-wrap items-center gap-1'>
            {visible}
            {overflow > 0 && <span className='text-xs text-muted-foreground'>+{overflow}</span>}
          </div>
        );
      },
      size: 220
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: ({ row }) => <TicketStatusBadge status={row.original.status} />,
      meta: { orderingKey: 'status' },
      size: 120
    },
    {
      id: 'dueAt',
      header: 'Due at',
      accessorKey: 'dueAt',
      cell: ({ row }) => {
        const { dueAt, status } = row.original;
        if (!dueAt) {
          return null;
        }
        const isOverdue = ACTIVE_TICKET_STATUSES.has(status) && new Date(dueAt).getTime() < Date.now();
        return (
          <div className='flex flex-col items-start'>
            <span>{noddiFormatDate(dueAt, NoddiDateFormats.DATE)}</span>
            {isOverdue && <span className='text-xs text-dangerDark'>Overdue</span>}
          </div>
        );
      },
      meta: { orderingKey: 'due_at' },
      size: 140
    },
    {
      id: 'category',
      header: 'Category',
      accessorFn: (row) => TICKET_CATEGORY_LABELS[row.category] ?? row.category,
      meta: { orderingKey: 'category' },
      size: 140
    },
    {
      id: 'updatedAt',
      header: 'Updated at',
      accessorKey: 'updatedAt',
      meta: { type: 'date-time', orderingKey: 'updated_at' },
      size: 140
    }
  ];
};
